// Small annotation editor for the screenshot: draw rectangles to highlight a spot or to
// black out personal data before anything leaves the browser.
import type { Rect } from '../shared/types.ts';
import { h, icon, uid } from './dom.ts';
import { type Annotation, type Capture, compose, drawAnnotation } from './screenshot.ts';
import { T } from './texts.ts';

export interface EditorOptions {
  mount: HTMLElement;
  capture: Capture;
  elementRects: Rect[];
  annotations: Annotation[];
}

const MIN_SIZE = 6;

function normalise(a: { x: number; y: number }, b: { x: number; y: number }): Rect {
  return { x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), width: Math.abs(a.x - b.x), height: Math.abs(a.y - b.y) };
}

/** Resolves with the edited annotation list once the user closes the editor. */
export function editScreenshot({ mount, capture, elementRects, annotations: initial }: EditorOptions): Promise<Annotation[]> {
  return new Promise((resolve) => {
    const annotations = [...initial];
    let tool: Annotation['kind'] = 'mark';
    let start: { x: number; y: number } | null = null;
    let draft: Annotation | null = null;
    let base = compose(capture, elementRects, annotations);

    const canvas = h('canvas', { class: 'gsb-editor-canvas', attrs: { role: 'img', 'aria-label': T.screenshotPreviewAlt } });
    canvas.width = base.width;
    canvas.height = base.height;
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;

    const titleId = uid('editor-title');
    const toolButton = (kind: Annotation['kind'], label: string, iconName: 'square' | 'redact') =>
      h('button', { class: 'gsb-tool', attrs: { type: 'button', 'aria-pressed': String(kind === tool), 'data-kind': kind } }, [icon(iconName, 18), label]);
    const markBtn = toolButton('mark', T.editorMark, 'square');
    const redactBtn = toolButton('redact', T.editorRedact, 'redact');
    const undoBtn = h('button', { class: 'gsb-tool', attrs: { type: 'button' } }, [icon('undo', 18), T.editorUndo]);
    const doneBtn = h('button', { class: 'gsb-btn gsb-btn-primary', attrs: { type: 'button' } }, [icon('check', 18), T.editorDone]);

    const dialog = h('div', { class: 'gsb-editor', attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': titleId } }, [
      h('div', { class: 'gsb-editor-head' }, [
        h('h2', { class: 'gsb-editor-title', text: T.editorTitle, attrs: { id: titleId } }),
        h('p', { class: 'gsb-editor-hint', text: T.editorHint }),
      ]),
      h('div', { class: 'gsb-editor-tools', attrs: { role: 'toolbar', 'aria-label': T.editorTitle } }, [markBtn, redactBtn, undoBtn]),
      h('div', { class: 'gsb-editor-stage' }, [canvas]),
      h('div', { class: 'gsb-editor-foot' }, [doneBtn]),
    ]);
    const backdrop = h('div', { class: 'gsb-editor-backdrop', attrs: { 'data-gsb-ignore': '' } }, [dialog]);
    mount.append(backdrop);

    function redraw(): void {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(base, 0, 0);
      if (draft) drawAnnotation(ctx, draft, capture.scale);
      undoBtn.disabled = annotations.length === 0;
    }

    function commit(): void {
      base = compose(capture, elementRects, annotations);
      redraw();
    }

    function toCapture(event: PointerEvent): { x: number; y: number } {
      const r = canvas.getBoundingClientRect();
      const x = ((event.clientX - r.left) / r.width) * (canvas.width / capture.scale);
      const y = ((event.clientY - r.top) / r.height) * (canvas.height / capture.scale);
      return { x: Math.max(0, Math.min(capture.width, x)), y: Math.max(0, Math.min(capture.height, y)) };
    }

    function setTool(kind: Annotation['kind']): void {
      tool = kind;
      markBtn.setAttribute('aria-pressed', String(kind === 'mark'));
      redactBtn.setAttribute('aria-pressed', String(kind === 'redact'));
    }

    function close(): void {
      document.removeEventListener('keydown', onKey, true);
      backdrop.remove();
      resolve(annotations);
    }

    function onKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        close();
      } else if (event.key === 'Tab') {
        // Keep focus inside the modal editor.
        const focusable = [markBtn, redactBtn, undoBtn, doneBtn].filter((b) => !b.disabled);
        const active = (mount.getRootNode() as ShadowRoot).activeElement;
        const index = focusable.indexOf(active as HTMLButtonElement);
        const next = event.shiftKey ? (index <= 0 ? focusable.length - 1 : index - 1) : (index + 1) % focusable.length;
        event.preventDefault();
        focusable[next]?.focus();
      }
    }

    canvas.addEventListener('pointerdown', (event) => {
      canvas.setPointerCapture(event.pointerId);
      start = toCapture(event);
      draft = { kind: tool, rect: { ...start, width: 0, height: 0 } };
    });
    canvas.addEventListener('pointermove', (event) => {
      if (!start || !draft) return;
      draft.rect = normalise(start, toCapture(event));
      redraw();
    });
    const endDraw = () => {
      if (draft && draft.rect.width >= MIN_SIZE && draft.rect.height >= MIN_SIZE) annotations.push(draft);
      start = null;
      draft = null;
      commit();
    };
    canvas.addEventListener('pointerup', endDraw);
    canvas.addEventListener('pointercancel', endDraw);

    markBtn.addEventListener('click', () => setTool('mark'));
    redactBtn.addEventListener('click', () => setTool('redact'));
    undoBtn.addEventListener('click', () => {
      annotations.pop();
      commit();
    });
    doneBtn.addEventListener('click', close);
    backdrop.addEventListener('click', (event) => {
      if (event.target === backdrop) close();
    });
    document.addEventListener('keydown', onKey, true);

    redraw();
    markBtn.focus();
  });
}
